import { useTeams } from '@/domains/teams';
import { useUsers } from '@/domains/users';
import { PageHeader } from '@/shared/components/PageHeader';
import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Clock, Loader2, Pencil, Plus, Search, Trash2, Users, X } from 'lucide-react';
import { useState } from 'react';
import type {
  AssignShiftInput,
  AssignShiftToTeamInput,
  ShiftDetail,
  UpdateShiftInput,
} from '../types/shift';

interface ShiftDetailViewProps {
  shift: ShiftDetail | null;
  isLoading?: boolean;
  onBack: () => void;
  onUpdateShift?: (data: UpdateShiftInput) => Promise<void>;
  onAssignShift?: (data: AssignShiftInput) => Promise<void>;
  onAssignShiftToTeam?: (data: AssignShiftToTeamInput) => Promise<void>;
  onDeleteAssignment?: (id: number) => Promise<void>;
}

// Format date for display (extract YYYY-MM-DD from ISO string)
const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  // Handle both ISO timestamps and simple date strings
  return dateString.split('T')[0];
};

export function ShiftDetailView({
  shift,
  isLoading = false,
  onBack,
  onUpdateShift,
  onAssignShift,
  onAssignShiftToTeam,
  onDeleteAssignment,
}: ShiftDetailViewProps) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAssignTeamModal, setShowAssignTeamModal] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [isAssignSubmitting, setIsAssignSubmitting] = useState(false);
  const [isAssignTeamSubmitting, setIsAssignTeamSubmitting] = useState(false);
  const [editForm, setEditForm] = useState<UpdateShiftInput>({});
  const [assignUserSearch, setAssignUserSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [assignStartDate, setAssignStartDate] = useState('');
  const [assignEndDate, setAssignEndDate] = useState('');
  const [assignTeamSearch, setAssignTeamSearch] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [assignTeamStartDate, setAssignTeamStartDate] = useState('');
  const [assignTeamEndDate, setAssignTeamEndDate] = useState('');

  // Search users on the server, not client-side
  const { data: usersData, isLoading: isUsersLoading } = useUsers({
    search: assignUserSearch || undefined,
    limit: 50, // Show more users
  });
  const rawUsers = usersData?.users ?? [];

  // Transform users to add fullName
  const users = rawUsers.map((user) => ({
    ...user,
    fullName: `${user.firstName ?? ''} ${user.secondName ?? ''}`.trim() || 'Unknown User',
  }));

  // Search teams on the server
  const { data: teams = [], isLoading: isTeamsLoading } = useTeams();

  // No need to filter - backend already filtered by search
  const filteredUsers = users;

  // For teams, we still filter client-side since there's no search endpoint
  const filteredTeams = teams.filter((team) => {
    if (!assignTeamSearch) return true;
    return team.name?.toLowerCase().includes(assignTeamSearch.toLowerCase());
  });

  const openEditModal = () => {
    setEditForm({
      title: shift?.title ?? '',
      code: shift?.code ?? undefined,
      startTime: shift?.startTime ?? '',
      endTime: shift?.endTime ?? '',
      color: shift?.color ?? undefined,
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditForm({});
  };

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setSelectedUserId(null);
    setAssignStartDate('');
    setAssignEndDate('');
    setAssignUserSearch('');
  };

  const closeAssignTeamModal = () => {
    setShowAssignTeamModal(false);
    setSelectedTeamId(null);
    setAssignTeamStartDate('');
    setAssignTeamEndDate('');
    setAssignTeamSearch('');
  };

  const handleEditSubmit = async () => {
    if (!editForm.title?.trim()) return;
    setIsEditSubmitting(true);
    try {
      await onUpdateShift?.(editForm);
      closeEditModal();
      showSuccessToast('Shift Updated', 'Shift details updated successfully.');
    } catch (error: any) {
      showErrorToast('Failed to Update Shift', error?.message || 'An unexpected error occurred.');
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleAssignSubmit = async () => {
    if (!selectedUserId || !assignStartDate || !assignEndDate || !shift) return;
    setIsAssignSubmitting(true);
    try {
      await onAssignShift?.({
        shiftId: shift.id,
        userId: selectedUserId,
        startDate: assignStartDate,
        endDate: assignEndDate,
      });
      closeAssignModal();
      showSuccessToast('Shift Assigned', 'Shift assigned to user successfully.');
    } catch (error: any) {
      showErrorToast('Failed to Assign Shift', error?.message || 'An unexpected error occurred.');
    } finally {
      setIsAssignSubmitting(false);
    }
  };

  const handleAssignTeamSubmit = async () => {
    if (!selectedTeamId || !assignTeamStartDate || !assignTeamEndDate || !shift) return;
    setIsAssignTeamSubmitting(true);
    try {
      await onAssignShiftToTeam?.({
        shiftId: shift.id,
        teamId: selectedTeamId,
        startDate: assignTeamStartDate,
        endDate: assignTeamEndDate,
      });
      closeAssignTeamModal();
      showSuccessToast('Shift Assigned', 'Shift assigned to team successfully.');
    } catch (error: any) {
      showErrorToast('Failed to Assign Team', error?.message || 'An unexpected error occurred.');
    } finally {
      setIsAssignTeamSubmitting(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId: number) => {
    try {
      await onDeleteAssignment?.(assignmentId);
      showSuccessToast('Assignment Removed', 'Shift assignment removed successfully.');
    } catch (error: any) {
      showErrorToast(
        'Failed to Remove Assignment',
        error?.message || 'An unexpected error occurred.',
      );
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  if (!shift) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Shift not found</p>
          <Button variant="outline" className="mt-4" onClick={onBack}>
            Back to Shifts
          </Button>
        </div>
      </div>
    );
  }

  const assignments = shift.assignments ?? [];

  return (
    <>
      <div className="h-full flex flex-col overflow-hidden">
        <PageHeader
          title={shift.title}
          subtitle={`${shift.startTime} - ${shift.endTime}`}
          icon={<Clock className="w-7 h-7 text-orange-600 dark:text-orange-400" />}
          breadcrumbs={[
            { label: 'Shift Management', onClick: onBack },
            { label: shift.title, current: true },
          ]}
          actions={
            <div className="flex gap-2">
              <Button variant="outline" onClick={openEditModal}>
                <Pencil className="w-4 h-4 mr-2" />
                Edit Shift
              </Button>
              <Button variant="outline" onClick={() => setShowAssignModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Assign to User
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={() => setShowAssignTeamModal(true)}
              >
                <Users className="w-4 h-4 mr-2" />
                Assign to Team
              </Button>
            </div>
          }
        />

        <div className="flex-1 overflow-y-auto">
          {/* Shift Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Shift Code</div>
              <div className="text-2xl font-bold text-foreground">{shift.code || '—'}</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Start Time</div>
              <div className="text-2xl font-bold text-foreground">{shift.startTime}</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">End Time</div>
              <div className="text-2xl font-bold text-foreground">{shift.endTime}</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Colour</div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded" style={{ backgroundColor: shift.color }} />
                <span className="text-sm text-muted-foreground">{shift.color}</span>
              </div>
            </div>
          </div>

          {/* Assignments List */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Team Assignments</h3>
              <Badge
                variant="outline"
                className="bg-blue-50 dark:bg-blue-950/30 text-blue-600 border-blue-200 dark:border-blue-800"
              >
                {assignments.length} {assignments.length === 1 ? 'assignment' : 'assignments'}
              </Badge>
            </div>

            <div className="space-y-3">
              {assignments.map((assignment) => (
                <div
                  key={assignment.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border hover:bg-muted/70 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground">{assignment.teamName}</div>
                    <div className="text-sm text-muted-foreground">
                      {assignment.userName ?? 'All team members'} &middot;{' '}
                      {formatDate(assignment.startDate)} to {formatDate(assignment.endDate)}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-4 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                    onClick={() => handleDeleteAssignment(assignment.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}

              {assignments.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No assignments for this shift yet</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setShowAssignTeamModal(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Assign to Team
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Shift Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border border-border shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Edit Shift</h2>
                <p className="text-sm text-muted-foreground">Update shift details</p>
              </div>
              <button
                onClick={closeEditModal}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Shift Title <span className="text-red-500">*</span>
                </label>
                <Input
                  value={editForm.title ?? ''}
                  onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Enter shift title"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Shift Code
                </label>
                <Input
                  value={editForm.code ?? ''}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, code: e.target.value || undefined }))
                  }
                  placeholder="Optional code"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">
                    Start Time
                  </label>
                  <Input
                    type="time"
                    value={editForm.startTime ?? ''}
                    onChange={(e) => setEditForm((f) => ({ ...f, startTime: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">
                    End Time
                  </label>
                  <Input
                    type="time"
                    value={editForm.endTime ?? ''}
                    onChange={(e) => setEditForm((f) => ({ ...f, endTime: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-border">
              <Button variant="outline" onClick={closeEditModal}>
                Cancel
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={handleEditSubmit}
                disabled={!editForm.title?.trim() || isEditSubmitting}
              >
                {isEditSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Assign to User Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border border-border shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Assign Shift to User</h2>
                <p className="text-sm text-muted-foreground">
                  Select a user and date range for this shift
                </p>
              </div>
              <button
                onClick={closeAssignModal}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Select User *
                </label>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={assignUserSearch}
                    onChange={(e) => setAssignUserSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1 border border-border rounded-lg p-2">
                  {isUsersLoading ? (
                    <div className="text-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                      <p className="text-sm text-muted-foreground mt-2">Loading users...</p>
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      {assignUserSearch ? 'No users match your search' : 'No users found'}
                    </div>
                  ) : (
                    filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        className={`p-2 rounded cursor-pointer transition-all ${
                          selectedUserId === user.id
                            ? 'bg-orange-50 dark:bg-orange-950/20 border border-orange-600'
                            : 'hover:bg-muted/50 border border-transparent'
                        }`}
                        onClick={() => setSelectedUserId(user.id)}
                      >
                        <div className="font-medium text-sm text-foreground">{user.fullName}</div>
                        <div className="text-xs text-muted-foreground">
                          {user.jobTitle || 'No job title'}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">
                    Start Date *
                  </label>
                  <Input
                    type="date"
                    value={assignStartDate}
                    onChange={(e) => setAssignStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">
                    End Date *
                  </label>
                  <Input
                    type="date"
                    value={assignEndDate}
                    onChange={(e) => setAssignEndDate(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-border">
              <Button variant="outline" onClick={closeAssignModal}>
                Cancel
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={handleAssignSubmit}
                disabled={
                  !selectedUserId || !assignStartDate || !assignEndDate || isAssignSubmitting
                }
              >
                {isAssignSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Assign Shift
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Assign to Team Modal */}
      {showAssignTeamModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border border-border shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Assign Shift to Team</h2>
                <p className="text-sm text-muted-foreground">
                  Creates assignments for all team members
                </p>
              </div>
              <button
                onClick={closeAssignTeamModal}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Select Team *
                </label>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search teams..."
                    value={assignTeamSearch}
                    onChange={(e) => setAssignTeamSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1 border border-border rounded-lg p-2">
                  {isTeamsLoading ? (
                    <div className="text-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                      <p className="text-sm text-muted-foreground mt-2">Loading teams...</p>
                    </div>
                  ) : filteredTeams.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      {assignTeamSearch ? 'No teams match your search' : 'No teams found'}
                    </div>
                  ) : (
                    filteredTeams.map((team) => (
                      <div
                        key={team.id}
                        className={`p-2 rounded cursor-pointer transition-all ${
                          selectedTeamId === team.id
                            ? 'bg-orange-50 dark:bg-orange-950/20 border border-orange-600'
                            : 'hover:bg-muted/50 border border-transparent'
                        }`}
                        onClick={() => setSelectedTeamId(team.id)}
                      >
                        <div className="font-medium text-sm text-foreground">{team.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {team.memberCount} members
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">
                    Start Date *
                  </label>
                  <Input
                    type="date"
                    value={assignTeamStartDate}
                    onChange={(e) => setAssignTeamStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">
                    End Date *
                  </label>
                  <Input
                    type="date"
                    value={assignTeamEndDate}
                    onChange={(e) => setAssignTeamEndDate(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-border">
              <Button variant="outline" onClick={closeAssignTeamModal}>
                Cancel
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={handleAssignTeamSubmit}
                disabled={
                  !selectedTeamId ||
                  !assignTeamStartDate ||
                  !assignTeamEndDate ||
                  isAssignTeamSubmitting
                }
              >
                {isAssignTeamSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Assign to Team
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
